/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import ApproveViewsButton from "./approve.view.buttons";
import ApproveButton from "./approve.user";
import dataProvider from "../../authentication/dataProvider";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Filter, Eye } from "lucide-react";
import { UserRecord } from "../../types/users/users";
import CustomLoader from "../ui/custom-loader";
import ApproveUserAutomate from "./approveUserAutomate";

const Users = () => {
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);
  const [filterRoleId, setFilterRoleId] = React.useState<string>("");
  const [filterLabel, setFilterLabel] = React.useState<string>("Filter");
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(20);
  const navigate = useNavigate();

  const handleFilterSelect = (id: string, label: string) => {
    setFilterRoleId(id);
    setFilterLabel(label || "Filter");
    setPage(1); // Reset to first page when filter changes
  };

  const { data, isLoading } = useQuery({
    queryKey: ["users", filterRoleId, page, perPage],
    queryFn: async () => {
      const response = await dataProvider.getList("users", {
        pagination: { page, perPage },
        sort: { field: "id", order: "DESC" },
        filter: filterRoleId ? { roleId: filterRoleId } : {},
      });
      return {
        data: (response?.data || []).map((user) => {
          // Extract governmentId with proper handling
          const govId = user.governmentId || (user as any)?.government_id || "";
          const processedGovId =
            govId &&
            String(govId).trim() !== "" &&
            String(govId) !== "null" &&
            String(govId) !== "undefined"
              ? String(govId)
              : "";

          return {
            ...user,
            name: user.name || "",
            profile: user.profile || "",
            status: user.status || "",
            role: {
              roleName: user.role?.roleName || "",
            },
            walletAddress: user.walletAddress || "",
            isRequestedForInvestor: user.isRequestedForInvestor || false,
            governmentId: processedGovId,
          };
        }),
        total: response?.total || 0,
      };
    },
  });

  // const users = data || [];
  const users = data?.data || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / perPage);

  const handleToggleItem = (record: UserRecord) => {
    setSelectedUsers((prev) =>
      prev.includes(record.id)
        ? prev.filter((id) => id !== record.id)
        : [...prev, record.id],
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <CustomLoader size="lg" />
        </CardContent>
      </Card>
    );
  }

  if (!Array.isArray(users) || users.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>List of Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">No users found</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>List of Users</CardTitle>
        <div className="flex gap-2 items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                {filterLabel}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  handleFilterSelect("67ee40a0f24f134120265548", "Investors")
                }
              >
                Investors
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  handleFilterSelect("67f3d607b344522c87c73f9a", "Users")
                }
              >
                Users
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleFilterSelect("", "Filter")}
              >
                Clear Filter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {selectedUsers.length > 0 && (
            <ApproveViewsButton selectedIds={selectedUsers} resource="users" />
          )}
          <ApproveUserAutomate />
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader className="items-center">
              <TableRow>
                {/* <TableHead className="w-[50px]">Select</TableHead> */}
                <TableHead className="text-center">Profile</TableHead>
                <TableHead className="text-center">Name</TableHead>
                <TableHead className="text-center">Email</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Role</TableHead>
                <TableHead className="text-center">Wallet Address</TableHead>
                <TableHead className="text-center">View</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user: UserRecord) => (
                <TableRow key={user?._id || user?.id}>
                  {/* <TableCell>
                    {user.status !== 'pending' && user.status !== 'APPROVED' && (
                      <Checkbox
                        disabled={
                          (user?.status === 'APPROVED' && user?.role?.roleName === 'CREATOR') || 
                          (user?.role?.roleName === 'COLLECTOR')
                        }
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={() => handleToggleItem(user)}
                      />
                    )}
                  </TableCell> */}
                  <TableCell>
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user?.profile} alt={user?.name || ""} />
                      <AvatarFallback>
                        {user?.name ? user.name.charAt(0).toUpperCase() : "?"}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="text-center">
                    {user?.name || "---"}
                  </TableCell>
                  <TableCell className="text-center">
                    {user?.email || "---"}
                  </TableCell>
                  <TableCell className="text-center">{user?.status}</TableCell>
                  <TableCell className="text-center">
                    {user?.role?.roleName}
                  </TableCell>
                  <TableCell className="font-mono text-center">
                    {user?.walletAddress}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 items-center justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/users/${user.id}`)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 items-center justify-center">
                      <ApproveButton record={user} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Showing{" "}
          <span className="font-medium">{(page - 1) * perPage + 1}</span> to{" "}
          <span className="font-medium">{Math.min(page * perPage, total)}</span>{" "}
          of <span className="font-medium">{total}</span> users
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <div className="flex items-center justify-center text-sm font-medium">
            Page {page} of {pageCount || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => (p < pageCount ? p + 1 : p))}
            disabled={page >= pageCount}
          >
            Next
          </Button>
          <select
            className="h-8 w-[70px] rounded-md border border-input bg-background px-2"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1); // Reset to first page when changing items per page
            }}
          >
            {[10, 20, 30].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Card>
  );
};

export default Users;
